// Original file retained at ./progressive-diffs-without-loading-optimizations.ts
// Guarded with a feature flag `pr_react_loading_optimizations`
import {ProgressiveLoadingStatus, type ProgressiveDiffEntry} from '../../types/progressive-diff-types'
import {contextRangesForMarkers} from '../../utils/context-ranges-for-markers'
import {buildProgressiveDiffEntries} from '../../utils/build-progressive-diff-entries'
import {identifyDiffEntriesToLoad} from '../../utils/identify-diff-entries-to-load'
import {scheduleLowPriorityUpdate} from '../../utils/schedule-low-priority-update'
import type {PullRequestFileTreeDiff} from '../../page-data/payloads/file-tree'
import type {StateCreator} from 'zustand'
import type {DiffEntriesSlice} from './diff-entries'
import type {DiffSummariesSlice, DiffSummariesSliceOpts} from './diff-summaries'

export interface ProgressiveDiffsSlice {
  entries: ProgressiveDiffEntry[]
  selectedPathDigest: string | undefined

  // Public actions, intended for use outside the store.
  progressiveDiffsActions: {
    loadMore: (startingAt: ProgressiveDiffEntry, options?: {preferAnySoloEntry?: boolean}) => Promise<void>
    updateSelectedPathDigest: (pathDigest: string | undefined) => void
  }

  // Private actions, intended for use by public actions.
  internalProgressiveDiffsActions: {
    loadSelectedEntries: (selectedEntries: ProgressiveDiffEntry[]) => void | Promise<void>
  }
}

export type ProgressiveDiffSliceOpts = {
  prReactLoadingOptimizations?: boolean | undefined
  initialLoadedPathDigests: Set<string>
  selectedPathDigest: string | undefined
} & Pick<DiffSummariesSliceOpts, 'initialDiffSummaries'>

export const createProgressiveDiffsSlice =
  ({
    initialDiffSummaries,
    initialLoadedPathDigests,
    selectedPathDigest,
  }: ProgressiveDiffSliceOpts): StateCreator<
    ProgressiveDiffsSlice & DiffEntriesSlice & DiffSummariesSlice,
    [['zustand/devtools', never]],
    [],
    ProgressiveDiffsSlice
  > =>
  (set, get) => {
    // Track path digests that have been requested but whose loading state has not yet been written to
    // the store (because the low priority update may still be deferred). This prevents duplicate network
    // requests when subsequent interactions trigger loading before the idle callback runs.
    const inFlightPathDigests = new Set<string>()

    const removeFromInFlight = (digests: Iterable<string>) => {
      for (const pathDigest of digests) {
        inFlightPathDigests.delete(pathDigest)
      }
    }

    const getLoadingStateSets = () => {
      const loaded = new Set<string>()
      const loading = new Set<string>()
      for (const entry of get().entries) {
        if (entry.loadingStatus === ProgressiveLoadingStatus.Loaded) {
          loaded.add(entry.pathDigest)
        } else if (entry.loadingStatus === ProgressiveLoadingStatus.Loading) {
          loading.add(entry.pathDigest)
        }
      }
      return {loaded, loading}
    }

    // Cache the Object.values() conversion of diffSummaries to avoid recomputing it when the diffSummaries object hasn't changed.
    let cachedDiffSummariesRef: Record<string, PullRequestFileTreeDiff> | undefined = initialDiffSummaries
    let cachedDiffSummariesArray: PullRequestFileTreeDiff[] = Object.values(initialDiffSummaries)

    const getDiffSummariesArray = (diffSummaries?: Record<string, PullRequestFileTreeDiff>) => {
      const source = diffSummaries ?? get().diffSummaries
      if (!source) return []
      if (cachedDiffSummariesRef === source) return cachedDiffSummariesArray
      cachedDiffSummariesRef = source
      cachedDiffSummariesArray = Object.values(source)
      return cachedDiffSummariesArray
    }

    const entries = buildProgressiveDiffEntries({
      diffSummaries: initialDiffSummaries,
      diffSummariesArray: cachedDiffSummariesArray,
      loadedPathDigests: initialLoadedPathDigests,
      loadingPathDigests: new Set<string>(),
      selectedPathDigest,
    })

    const scheduleEntriesUpdate = (
      {
        loadedPathDigests,
        loadingPathDigests,
      }: {
        loadedPathDigests: Iterable<string>
        loadingPathDigests: Iterable<string>
      },
      reason: string,
      options?: Parameters<typeof scheduleLowPriorityUpdate>[1],
    ) => {
      const loadedSnapshot = Array.from(loadedPathDigests)
      const loadingSnapshot = Array.from(loadingPathDigests)

      scheduleLowPriorityUpdate(
        () =>
          set(
            state => {
              const nextEntries = buildProgressiveDiffEntries({
                diffSummaries: state.diffSummaries,
                diffSummariesArray: getDiffSummariesArray(state.diffSummaries),
                loadedPathDigests: new Set(loadedSnapshot),
                loadingPathDigests: new Set(loadingSnapshot),
                selectedPathDigest: state.selectedPathDigest,
              })

              return {
                ...state,
                entries: nextEntries,
              }
            },
            undefined,
            reason,
          ),
        options,
      )
    }

    return {
      entries,
      selectedPathDigest,
      internalProgressiveDiffsActions: {
        loadSelectedEntries: async (selectedEntries: ProgressiveDiffEntry[]) => {
          if (selectedEntries.length === 0) return

          // Avoid re-requesting entries that are already in progress. We may receive repeated calls while
          // the idle callback is pending, so we bail early if everything is already tracked as in-flight
          const entriesToFetch = selectedEntries.filter(entry => !inFlightPathDigests.has(entry.pathDigest))
          if (entriesToFetch.length === 0) return

          const pathsToFetch = entriesToFetch.map(e => e.path)
          const pathDigestsToFetch = entriesToFetch.map(e => e.pathDigest)

          // Mark these entries as in-flight immediately, before any async work begins
          // This ensures subsequent calls see them as already being loaded
          for (const pathDigest of pathDigestsToFetch) {
            inFlightPathDigests.add(pathDigest)
          }

          const {loaded: previouslyLoadedPathDigests, loading: previouslyLoadingPathDigests} = getLoadingStateSets()
          // Merge the freshly marked in-flight entries with the existing loading digests
          const loadingPathDigests = new Set<string>([...previouslyLoadingPathDigests, ...inFlightPathDigests])

          scheduleEntriesUpdate(
            {
              loadedPathDigests: previouslyLoadedPathDigests,
              loadingPathDigests,
            },
            'loadSelectedEntries#preFetch',
          )

          const {loadDiffEntries} = get().diffEntriesActions
          const contextRanges = contextRangesForMarkers({
            paths: new Set(pathsToFetch),
            diffSummaries: get().diffSummaries,
          })
          let newlyLoadedDiffEntries
          try {
            newlyLoadedDiffEntries = await loadDiffEntries({
              paths: pathsToFetch,
              contextRanges,
            })
          } catch (error) {
            removeFromInFlight(pathDigestsToFetch)
            throw error
          }

          const loadedDiffEntries = newlyLoadedDiffEntries ?? []
          const newlyLoadedPathDigestSet = new Set(loadedDiffEntries.map(ne => ne.pathDigest))

          removeFromInFlight(pathDigestsToFetch)

          const {loaded: loadedAfterRequest, loading: loadingAfterRequest} = getLoadingStateSets()

          // Build the new loaded set: previous + newly loaded entries.
          const currentLoadedPathDigests = new Set<string>(loadedAfterRequest)
          for (const pathDigest of newlyLoadedPathDigestSet) {
            currentLoadedPathDigests.add(pathDigest)
          }

          const currentLoadingPathDigests = new Set<string>(loadingAfterRequest)
          // Remove those that have just finished loading
          for (const pathDigest of newlyLoadedPathDigestSet) {
            currentLoadingPathDigests.delete(pathDigest)
          }

          // Preserve any entries that remain in-flight
          for (const pathDigest of inFlightPathDigests) {
            currentLoadingPathDigests.add(pathDigest)
          }

          scheduleEntriesUpdate(
            {
              loadedPathDigests: currentLoadedPathDigests,
              loadingPathDigests: currentLoadingPathDigests,
            },
            loadedDiffEntries.length === 0
              ? 'loadSelectedEntries#postFetch#noAPIData'
              : 'loadSelectedEntries#postFetch',
          )
        },
      },
      progressiveDiffsActions: {
        loadMore: async (
          startingAt: ProgressiveDiffEntry,
          {preferAnySoloEntry}: {preferAnySoloEntry?: boolean} = {},
        ) => {
          const entriesToLoad = identifyDiffEntriesToLoad({
            progressiveDiffEntries: get().entries,
            startingAt,
            preferAnySoloEntry,
          })

          return get().internalProgressiveDiffsActions.loadSelectedEntries(entriesToLoad)
        },
        updateSelectedPathDigest: (pathDigest: string | undefined) => {
          set((prevState: ProgressiveDiffsSlice) => ({
            ...prevState,
            selectedPathDigest: pathDigest,
          }))
          const selectedEntry = get().entries.find(e => e.pathDigest === pathDigest)
          if (selectedEntry) {
            return get().internalProgressiveDiffsActions.loadSelectedEntries([selectedEntry])
          }
        },
      },
    }
  }
